import { RankedLeaderboardEntry } from "@/src/models/leaderboard.model";
import { useTheme } from "@/src/styles/theme-provider";
import { CloseCircle, Cup, MedalStar, Star } from "iconsax-react-nativejs";
import { useTranslation } from "react-i18next";
import { Pressable, Text, View } from "react-native";
import BlocksAvatar from "@/src/components/ui-kit/blocks-avatar";

type LeaderboardUserPanelProps = Readonly<{
  entry: RankedLeaderboardEntry;
  isCurrentUser?: boolean;
  onClose: () => void;
}>;

const RANK_BADGE_BG: Readonly<Record<number, string>> = {
  1: "bg-wheat",
  2: "bg-ash-3",
  3: "bg-coral",
};

const RANK_LABEL: Readonly<Record<number, string>> = {
  1: "Gold",
  2: "Silver",
  3: "Bronze",
};

export const LeaderboardUserPanel = ({
  entry,
  isCurrentUser,
  onClose,
}: LeaderboardUserPanelProps) => {
  const { themeVars } = useTheme();
  const { t } = useTranslation();

  const headerTextColor = themeVars["--color-onyx-default"] as string;
  const headerTextMutedColor =
    `${themeVars["--color-onyx-default"]}99` as string;

  const badgeBg = RANK_BADGE_BG[entry?.rank];
  const rankLabel = RANK_LABEL[entry?.rank] ?? `#${entry?.rank}`;
  const formattedPoints = entry?.points?.toLocaleString() ?? "0";
  const achievementsCount = entry?.achievements ?? 0;
  const displayName = isCurrentUser ? t("LABEL_YOU") : entry?.displayName;

  return (
    <View className="self-start w-full rounded-2xl overflow-hidden bg-chalk md:border md:border-ash-3">
      <View className="items-center px-4 pb-5 pt-6 gap-2 bg-ash">
        <Pressable
          onPress={onClose}
          className="absolute top-3 right-3 hover:bg-ash-3 hover:rounded-full"
          hitSlop={8}
        >
          <CloseCircle size={22} color={headerTextColor} />
        </Pressable>

        <View
          className="w-16 h-16 rounded-full border-2 overflow-hidden items-center justify-center"
          style={{ borderColor: headerTextMutedColor }}
        >
          <BlocksAvatar
            userId={entry?.userId}
            fallbackImageUrl={entry?.avatar}
            name={entry?.displayName}
            size={64}
          />
        </View>

        <Text
          className="font-semibold text-sm text-center"
          style={{ color: headerTextColor }}
          numberOfLines={1}
        >
          {displayName}
        </Text>

        {isCurrentUser && (
          <Text
            className="text-xs text-center"
            style={{ color: headerTextMutedColor }}
          >
            {entry?.displayName}
          </Text>
        )}
      </View>

      {/* Stats strip */}
      <View className="flex-row border-b border-ash-3">
        <View className="flex-1 items-center py-3 border-r border-ash-3">
          <Text className="font-bold text-base text-primary">
            {formattedPoints}
          </Text>
          <Text className="text-xs tracking-wider text-slate">
            {t("LABEL_COL_POINTS")}
          </Text>
        </View>
        <View className="flex-1 items-center py-3">
          <Text className="font-bold text-base text-primary">
            {achievementsCount}
          </Text>
          <Text className="text-xs tracking-wider text-slate">
            {t("LABEL_BADGES")}
          </Text>
        </View>
      </View>

      {/* Detail rows */}
      <View>
        <View className="flex-row items-center justify-between px-4 py-3 border-b border-ash-3">
          <View className="flex-row items-center gap-2">
            <Cup
              size={20}
              color={themeVars["--color-slate-default"]}
              variant="Bold"
            />
            <Text className="text-xs text-slate">{t("LABEL_RANK")}</Text>
          </View>
          <View className="flex-row items-center gap-2">
            {badgeBg && (
              <View
                className={`w-6 h-6 rounded-full items-center justify-center ${badgeBg}`}
              >
                <Text className="text-onyx font-bold text-xs">
                  {entry?.rank}
                </Text>
              </View>
            )}
            <Text className="text-xs font-semibold text-primary">
              {rankLabel}
            </Text>
          </View>
        </View>

        <View className="flex-row items-center justify-between px-4 py-3 border-b border-ash-3">
          <View className="flex-row items-center gap-2">
            <Star
              size={20}
              color={themeVars["--color-slate-default"]}
              variant="Bold"
            />
            <Text className="text-xs text-slate">{t("LABEL_COL_POINTS")}</Text>
          </View>
          <Text className="text-xs font-semibold text-primary">
            {formattedPoints}
          </Text>
        </View>

        <View className="flex-row items-center justify-between px-4 py-3">
          <View className="flex-row items-center gap-2">
            <MedalStar
              size={20}
              color={themeVars["--color-slate-default"]}
              variant="Bold"
            />
            <Text className="text-xs text-slate">{t("LABEL_BADGES")}</Text>
          </View>
          <Text className="text-xs font-semibold text-primary">
            {achievementsCount}
          </Text>
        </View>
      </View>
    </View>
  );
};
